import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Alert, Box, Stack, TextField, Typography } from '@mui/material';
import moment from 'moment';

import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import { CancelButton, DeleteButton } from '../../../components/mui/Buttons/Buttons';
import {
  revokeSkillxManualXpAward,
  getSkillxManualXpAwards,
} from '../../../redux/features/skillxSlice';
import { SX } from '../leaderboard/leaderboardData';

/**
 * Revoke a manual XP award.
 *
 * The counterweight to letting mentors award XP by hand: a mistyped 500 has to
 * be reversible, or the leaderboard stops being trusted. Revoking deletes the
 * ledger row and subtracts the points from the learner's total — the one
 * deliberate exception to the ledger being append-only, and confined to rows
 * this portal wrote.
 *
 * The audit row itself is NOT deleted. It stays, marked REVOKED with the reason
 * given here: the record that someone awarded 500 XP by mistake is exactly what
 * an audit is for.
 */
export default function RevokeXpDialog({ rowData, dispatchparams }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { loading } = useSelector((state) => state.skillx);

  const alreadyRevoked = rowData?.status === 'REVOKED';
  const reasonError = touched && reason.trim().length < 5;

  const handleRevoke = () => {
    setTouched(true);
    if (reason.trim().length < 5) return;

    dispatch(revokeSkillxManualXpAward({ id: rowData?.id, reason: reason.trim() })).then((res) => {
      if (res.payload?.status === 'success') {
        enqueueSnackbar(res.payload.message, { variant: 'success' });
        dispatch(getSkillxManualXpAwards(dispatchparams));
        setReason('');
        setTouched(false);
        setOpen(false);
      } else {
        enqueueSnackbar(res.payload?.message || 'Failed to revoke the award', {
          variant: 'error',
        });
      }
    });
  };

  return (
    <GlobalDialog
      title="Revoke XP Award"
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth="sm"
      closeButton
      popupButton={
        <DeleteButton size="small" disabled={alreadyRevoked} action={() => setOpen(true)}>
          {alreadyRevoked ? 'Revoked' : 'Revoke'}
        </DeleteButton>
      }
      content={
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Box
            sx={{
              p: 1.75,
              borderRadius: `${SX.radius.sm}px`,
              background: SX.cardSoft,
              border: `1px solid ${SX.border}`,
            }}
          >
            <Stack spacing={0.75}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: SX.text }}>
                  {rowData?.student_name || `Learner ${rowData?.admn_no}`}
                </Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: SX.blue }}>
                  {rowData?.points} XP
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: 12, color: SX.textMuted }}>
                {rowData?.rule_label || rowData?.rule_key} · {rowData?.batch_name || `Batch ${rowData?.batch_id}`}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: SX.text, fontStyle: 'italic' }}>
                “{rowData?.remarks}”
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: SX.textMuted }}>
                Awarded by {rowData?.awarded_by_name || rowData?.awarded_by} on{' '}
                {rowData?.awarded_at ? moment(rowData.awarded_at).format('DD MMM YYYY, hh:mm A') : '—'}
              </Typography>
            </Stack>
          </Box>

          {alreadyRevoked ? (
            <Alert severity="info" sx={{ fontSize: 12.5 }}>
              This award was already revoked by{' '}
              <b>{rowData?.revoked_by_name || rowData?.revoked_by}</b> —{' '}
              {rowData?.revoke_reason || 'no reason recorded'}.
            </Alert>
          ) : (
            <>
              <Alert severity="warning" sx={{ fontSize: 12.5 }}>
                <b>{rowData?.points} XP</b> will be taken off this learner&apos;s total and the
                ledger entry deleted. The award stays in this history, marked revoked with your
                reason.
              </Alert>

              <TextField
                label="Why is this being revoked?"
                fullWidth
                multiline
                minRows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                onBlur={() => setTouched(true)}
                error={reasonError}
                helperText={
                  reasonError
                    ? 'A reason is required — at least a few words'
                    : 'Recorded against your name alongside the original award'
                }
              />
            </>
          )}
        </Stack>
      }
      actionButton={
        <>
          <CancelButton action={() => setOpen(false)}>Close</CancelButton>
          {!alreadyRevoked && (
            <DeleteButton disabled={loading} action={handleRevoke}>
              {loading ? 'Revoking...' : 'Revoke XP'}
            </DeleteButton>
          )}
        </>
      }
    />
  );
}
